import { getRuntimeEnv } from '../config/runtimeConfig';

// Get runtime environment variables
const env = getRuntimeEnv();

// API Configuration
export const API_CONFIG = {
  BASE_URL: `${env.VITE_API_BASE_URL.replace(/\/+$/, '')}/v1`,
  TIMEOUT: 60000, // 1 minute
};

// App Configuration
export const APP_CONFIG = {
  NAME: env.VITE_APP_NAME,
  APP_URL: env.VITE_APP_URL,
  PLATFORM_NAME: env.VITE_PLATFORM_NAME,
};

// Log configuration on startup (only in development)
const meta = import.meta as { env: Record<string, string | boolean | undefined>; DEV?: boolean; MODE?: string };
if (meta.DEV) {
  console.log('App Configuration:', {
    API_BASE_URL: API_CONFIG.BASE_URL,
    APP_NAME: APP_CONFIG.NAME,
    APP_URL: APP_CONFIG.APP_URL,
    MODE: meta.MODE || 'unknown'
  });
}

// API Endpoints
export const API_ENDPOINTS = {
  // Admin endpoints
  ADMIN: {
    COMPANIES: '/admin/fetchCompanies',
    COMPANY_BY_ID: (id: string) => `/admin/fetchCompanies/${id}`,
    COMPANY_ADMIN_USER: (id: string) => `/admin/fetchCompanies/${id}/adminUser`,
    COMPANY_USERS: (id: string) => `/admin/fetchCompanies/${id}/users`,
    
    UPDATE_COMPANY: (id: string) => `/admin/updateCompany/${id}`,
    DELETE_COMPANY: (id: string) => `/admin/deleteCompany/${id}`,
    COMPANY_SUBSCRIPTION: (id: string) => `/admin/fetchCompanies/${id}/subscription`,
    PLANS: '/admin/fetchPlans',
    PLAN_BY_ID: (id: string) => `/admin/fetchPlans/${id}`,
    CREATE_PLAN: '/admin/createPlan',
    UPDATE_PLAN: (id: string) => `/admin/updatePlan/${id}`,
    DELETE_PLAN: (id: string) => `/admin/deletePlan/${id}`,
    API_KEYS: '/admin/fetchTokens',
    API_KEY_BY_ID: (id: string) => `/admin/fetchTokens/${id}`,
    API_KEYS_BY_COMPANY: (companyId: string) => `/admin/fetchTokens/company/${companyId}`,
    CREATE_API_KEY: '/admin/createToken',
    UPDATE_API_KEY: (id: string) => `/admin/updateToken/${id}`,
    UPDATE_API_KEY_STATUS: (id: string) => `/admin/updateToken/${id}`,
    REGENERATE_API_KEY: (id: string) => `/admin/regenerateToken/${id}`,
    USAGE_SUMMARY: '/admin/usage/summary',
    RECORD_USAGE: '/admin/record-usage',
    SEND_WELCOME_EMAIL: '/admin/sendWelcomeEmail',
    MY_SUBSCRIPTION: '/admin/mySubscription',
    COMPANY_USAGE: (companyId: string) => `/admin/usage/summary?company_id=${companyId}`,
    GET_USERS: '/admin/users',
    DELETE_USER: (id: string) => `/admin/users/${id}`,
    INVITE_USER: '/admin/users/invite',
  },
  // Public endpoints
  PUBLIC: {
    PLANS: '/public/fetchPlans',
  },
  // Payment Gateway endpoints
  PAYMENT_GATEWAY: {
    CREATE_SUBSCRIPTION: '/paymentGateway/zoho/subscriptions/create',
    CREATE_SUBSCRIPTION_HOSTED_PLAN: '/paymentGateway/zoho/subscriptions/hostedPage/create',
    CREATE_HOSTED_PAGE: '/paymentGateway/zoho/subscriptions/hostedPage/create',
    UPDATE_SUBSCRIPTION: '/paymentGateway/zoho/subscriptions/update',
    
    FETCH_SUBSCRIPTION: '/paymentGateway/zoho/subscriptions/fetch/',
    CANCEL_SUBSCRIPTION: '/paymentGateway/zoho/subscriptions/cancel',
    REACTIVATE_SUBSCRIPTION: '/paymentGateway/zoho/subscriptions/reactivate',
    VALIDATE_PAYMENT: '/paymentGateway/zoho/subscriptions/validatePayment',
    RETRIEVE_HOSTED_PAGE: '/paymentGateway/zoho/subscriptions/fetchByHostedId/',
    CREATE_CUSTOMER: '/paymentGateway/zohoCustomers',
    GET_SUBSCRIPTIONS: '/paymentGateway/zohoSubscriptions',
    GET_SUBSCRIPTION: (id: string) => `/paymentGateway/zohoSubscriptions/${id}`,
    UPDATE_SUBSCRIPTION_BY_ID: (id: string) => `/paymentGateway/zohoSubscriptions/${id}`,
    CANCEL_SUBSCRIPTION_BY_ID: (id: string) => `/paymentGateway/zohoSubscriptions/${id}/cancel`,
    REACTIVATE_SUBSCRIPTION_BY_ID: (id: string) => `/paymentGateway/zohoSubscriptions/${id}/reactivate`,
  },
  // Auth endpoints
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
  },
  // Public endpoints (additional)
  PUBLIC_EXTRA: {
    VERIFY_INVITATION: '/public/verifyInvitation',
    ACCEPT_INVITATION: '/public/acceptInvitation',
  },
};



// Root company configuration
// Root company UUID - the specific UUID for the root company that should be protected
export const ROOT_COMPANY_UUID = '00000000-0000-0000-0000-000000000001';

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  THEME_MODE: 'themeMode',
};

/**
 * Utility function to clear all authentication-related localStorage items
 * This ensures complete cleanup of user data during logout
 */
export const clearAllAuthData = () => {
  // Clear standard authentication tokens
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  
  // Clear legacy authentication data
  localStorage.removeItem('loggedInUserData');
  localStorage.removeItem('onboardingData');
  localStorage.removeItem('pendingPlanChange');
  
  // Clear any other user-related data
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.includes('user') || 
      key.includes('auth') || 
      key.includes('login') || 
      key.includes('session') ||
      key.includes('token')
    )) {
      keysToRemove.push(key);
    }
  }
  
  // Remove all identified user-related keys
  keysToRemove.forEach(key => {
    if (key !== STORAGE_KEYS.THEME_MODE) { // Preserve theme preference
      localStorage.removeItem(key);
    }
  });
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50],
};

// Status colors for chips
export const STATUS_COLORS = {
  ACTIVE: 'success',
  INACTIVE: 'error',
  PENDING: 'warning',
  SUSPENDED: 'error',
  TRIAL: 'warning',
  EXPIRED: 'error',
  ENABLED: 'success',
  DISABLED: 'error',
} as const;

// Industry options
export const INDUSTRY_OPTIONS = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Other',
];